// web/src/lib/sfx.ts
// Shared UI sound effects (clips from the iOS app).
//
// Mobile-safe design (2026-07-05): the old per-clip HTMLAudio approach assumed every
// play happened inside a gesture handler — false in practice ("pop"/"wow" fire after
// `await validatePdf(...)`, and "do it" races the view transition), so iOS silently
// blocked those while tap-synchronous clips kept working. All clips now play through
// ONE shared WebAudio context:
//   - a document-level pointerdown/keydown unlock resumes the context on the first
//     gesture anywhere and warms (fetch+decode) every registered clip,
//   - decoded buffers are cached, so playback from ANY call stack — async included —
//     is a synchronous BufferSource.start() that survives navigations.
// No AudioContext (jsdom, ancient browsers) → legacy per-clip HTMLAudio fallback.
// (iOS hardware silent-switch still mutes WebAudio — nothing a web page can do.)
import type { Mode } from "@/types/roast";

type CtxCtor = new () => AudioContext;

let ctx: AudioContext | null = null;
let unlockInstalled = false;
const registeredSrcs: string[] = [];
const buffers = new Map<string, Promise<AudioBuffer | null>>();

function ctxCtor(): CtxCtor | null {
  const g = globalThis as { AudioContext?: CtxCtor; webkitAudioContext?: CtxCtor };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = ctxCtor();
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

function loadBuffer(src: string): Promise<AudioBuffer | null> {
  const cached = buffers.get(src);
  if (cached) return cached;
  const c = ensureCtx();
  const p = !c
    ? Promise.resolve(null)
    : fetch(src)
        .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject(new Error(`${res.status}`))))
        .then((ab) => c.decodeAudioData(ab))
        .catch(() => {
          buffers.delete(src); // allow retry on a later play
          return null;
        });
  buffers.set(src, p);
  return p;
}

/** First-gesture unlock: resume the (autoplay-suspended) context and warm every clip. */
function unlock(): void {
  const c = ensureCtx();
  if (!c) return;
  if (c.state === "suspended") void c.resume();
  for (const src of registeredSrcs) void loadBuffer(src);
}

function installUnlock(): void {
  if (unlockInstalled || typeof document === "undefined") return;
  unlockInstalled = true;
  // Not {once:true} — resume can need repeats until the context actually runs; cheap.
  document.addEventListener("pointerdown", unlock, { capture: true, passive: true });
  document.addEventListener("keydown", unlock, { capture: true });
}

function makePlayer(src: string, volume: number): () => void {
  registeredSrcs.push(src);
  installUnlock();

  // Legacy fallback: one reused Audio element, rewound per play (pre-WebAudio behavior).
  let legacy: HTMLAudioElement | null = null;
  const playLegacy = (): void => {
    try {
      if (!legacy) {
        legacy = new Audio(src);
        legacy.volume = volume;
      }
      legacy.currentTime = 0;
      legacy.play()?.catch(() => {});
    } catch {
      /* no Audio (SSR/tests) — ignore */
    }
  };

  return () => {
    const c = ensureCtx();
    if (!c) { playLegacy(); return; }
    if (c.state === "suspended") void c.resume(); // gesture-stack plays self-unlock
    void loadBuffer(src).then((buf) => {
      if (!buf || c.state !== "running") return; // still locked → skip silently
      try {
        const source = c.createBufferSource();
        source.buffer = buf;
        const gain = c.createGain();
        gain.gain.value = volume;
        source.connect(gain);
        gain.connect(c.destination);
        source.start();
      } catch {
        /* never let a sound effect throw into UI code */
      }
    });
  };
}

/** Generic "tap" click sound for common buttons. */
export const playTap = makePlayer("/audio/TapPhoto.m4a", 0.5);

/** The "do it." CTA sound. */
export const playDoIt = makePlayer("/audio/do_it.mp3", 0.7);

/** Pop when an added/imported source thumbnail appears. */
export const playPop = makePlayer("/audio/pop.mp3", 0.6);

/** Comedic "wow" layered (quietly) under the pop when a thumbnail appears. */
export const playAnimeWow = makePlayer("/audio/anime_wow.mp3", 0.35);

/** Quack when a source thumbnail is removed. */
export const playQuack = makePlayer("/audio/quack_duck.mp3", 0.6);

/** Applause on the kengineer@ credit click. */
export const playApplause = makePlayer("/audio/solo_applause.mp3", 0.5);

/** Toll on entering the hidden extreme mode. */
export const playUndertakerBell = makePlayer("/audio/undertaker_bell.mp3", 0.7);

/** Energy-blast whoosh for the ignition/reveal transition (Processing → Player). */
export const playIgnition = makePlayer("/audio/sfx/transitions/core_whoosh.mp3", 0.7);

/** "The power of Gemini" sting, fired at the very end of generation to lead into the ignition. */
export const playPowerOfGemini = makePlayer("/audio/power_of_gemini.mp3", 0.85);

const playRoastAlert = makePlayer("/audio/alert.aac", 0.6);
const playToastRizz = makePlayer("/audio/rizz_sound.mp3", 0.6);

/** Reaction sound played when switching TO a mode (roast → alert, toast → rizz). */
export function playModeSound(mode: Mode): void {
  if (mode === "roast") playRoastAlert();
  else playToastRizz();
}
